import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ClientApiService, EncryptApiService } from '@anduin/api';
import { AuthService, PermissionsState } from '@anduin/auth';
import { ConfigService } from '@anduin/config';
import {
  AgGridCellTemplateRendererComponent,
  AgGridFactory,
  AgGridStateService,
  BridgeDatasource,
  EXCLUDE_COLUMN_FROM_EXPORT,
  HasuraBaseQuery,
  HasuraQuery,
  NUMBER_COLUMN,
  PinnedSide,
  addCellClassRules,
} from '@anduin/shared';
import { Store } from '@ngxs/store';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, ColumnApi, GridApi, GridOptions } from 'ag-grid-community';
import { Apollo } from 'apollo-angular';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface ClientOverviewEntity {
  id: number;
  key: string;
  name: string;
  fullName: string;
  outstandingInvoices: number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-clients-table',
  templateUrl: './clients-table.component.html',
  styleUrls: ['./clients-table.component.scss'],
  standalone: false,
})
export class ClientsTableComponent implements OnInit, OnDestroy {
  private _unsubscribe$: Subject<void> = new Subject<void>();
  private _agGridStateService: AgGridStateService;
  @ViewChild(AgGridAngular) agGrid: AgGridAngular;

  @Input() public dataSource$: Observable<any[]>;
  @Input() selectionGroup: string;

  columnFilterApplied$ = new Subject<boolean>();

  canLaunchClientPortal$: Observable<boolean> = this.store.select(
    PermissionsState.hasPermission(['payments_can_launch_client_portal_view']),
  );
  @ViewChild('actionsTemplate') actionsTemplate: TemplateRef<HTMLElement>;

  // TODO add back sort when building out new Client Table requirements
  // @Input() sortBy: string;

  canLaunchClientPortal: boolean;

  gridApi: GridApi;
  gridColumnApi: ColumnApi;

  private agGridFactory: AgGridFactory = new AgGridFactory();
  gridOptions: GridOptions = {
    ...this.agGridFactory.serverSideGridOption(),
    onFilterChanged: (event) => this.columnFilterApplied$.next(Object.keys(event.api.getFilterModel()).length > 0),
    suppressRowHoverHighlight: true,
  };
  defaultColumnDef: ColDef = this.agGridFactory.standardColumnDef();

  columnDefs: ColDef[] = [
    {
      headerName: 'Client',
      field: 'clientName',
      pinned: PinnedSide.LEFT,
      minWidth: 300,
      suppressColumnsToolPanel: true,
    },
    {
      headerName: 'Client ID',
      field: 'clientRef',
      pinned: PinnedSide.LEFT,
      minWidth: 200,
    },
    {
      headerName: 'Account Manager',
      field: 'managerName',
      minWidth: 350,
    },
    {
      ...NUMBER_COLUMN(),
      headerName: 'Outstanding Invoices',
      field: 'numOutstandingInvoices',
      minWidth: 150,
    },
    {
      headerName: 'Client Key',
      field: 'clientKey',
      minWidth: 278,
    },
  ];

  clientsQuery: HasuraBaseQuery = new HasuraBaseQuery('getClientOverviewInfo', 'client', [
    new HasuraQuery('id'),
    new HasuraQuery('clientRef'),
    new HasuraQuery('outstandingInvoices_aggregate', [
      new HasuraQuery('aggregate', [new HasuraQuery('count', null, null, 'numOutstandingInvoices')]),
    ]),
    new HasuraQuery('managerStaff', [new HasuraQuery('fullName', undefined, undefined, 'managerName')]),
    new HasuraQuery('clientKey', undefined, undefined, 'clientKey'),
    new HasuraQuery('name', undefined, undefined, 'clientName'),
  ]);

  bridgeDatasource: BridgeDatasource<ClientOverviewEntity> = new BridgeDatasource<ClientOverviewEntity>(this.apollo, this.clientsQuery);

  count = 0;

  @Input() set searchValue(searchValue: string) {
    if (searchValue) {
      this.bridgeDatasource.addSearchBarExternalFilter(
        'searchBar',
        ['name', 'clientRef', 'clientKey', 'fullName'],
        'text',
        'contains',
        searchValue,
      );
    } else {
      this.bridgeDatasource.clearAllExternalFilters();
    }

    if (this.count > 0) {
      this.gridApi.onFilterChanged();
    }

    this.count += 1;
  }

  constructor(
    public configService: ConfigService,
    private authService: AuthService,
    private clientApiService: ClientApiService,
    private encryptApiService: EncryptApiService,
    private cdref: ChangeDetectorRef,
    private store: Store,
    public apollo: Apollo,
  ) {}

  ngOnInit() {
    // Add actions column if client portal admin access is enabled.
    if (this.configService.clientPortalAdminAccessEnabled && !this.columnDefs.find((col) => col.field === 'actions')) {
      this.columnDefs.push({
        headerName: 'Actions',
        field: 'actions',
        cellClass: addCellClassRules(['ag-grid-cell-center-aligned-vertical', 'ag-grid-cell-center-aligned']),
        cellRendererParams: {
          getTemplate: () => this.actionsTemplate,
        },
        cellRenderer: AgGridCellTemplateRendererComponent,
        minWidth: 100,
        pinned: PinnedSide.RIGHT,
        resizable: false,
        sortable: false,
        suppressColumnsToolPanel: true,
        suppressMenu: true,
        suppressMovable: true,
        type: EXCLUDE_COLUMN_FROM_EXPORT,
        width: 100,
      });
    }

    this.canLaunchClientPortal$.pipe(takeUntil(this._unsubscribe$)).subscribe((canLaunchClientPortal) => {
      this.canLaunchClientPortal = canLaunchClientPortal;
    });
  }

  onGridReady(gridOptions: GridOptions): void {
    this.gridApi = gridOptions.api;
    this.gridColumnApi = gridOptions.columnApi;
    this.gridApi.setServerSideDatasource(this.bridgeDatasource);
    this._agGridStateService = new AgGridStateService(
      {
        tableName: 'clients',
        rememberColumnState: true,
        rememberColumnSort: true,
        rememberFilterModel: true,
      },
      this.store,
      this.agGrid,
    );
    this._agGridStateService.getAllEventListeners().pipe(takeUntil(this._unsubscribe$)).subscribe();
  }

  /**
   * Route to client portal invoices page.
   * The Staff user will be logged into the client portal and their actions will be audited
   * */
  navigateToClientPortal(clientId: number): void {
    this.encryptApiService
      .generateAdminClientUrl(this.authService.getStaffId(), clientId)
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(
        (encryptedUrl: string) => {
          window.open(encryptedUrl);
        },
        (error) => console.error(`Error generating admin url ${error}`),
      );
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
